import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Custom hook to initialize and manage Google Analytics 4 (GA4) tracking
 * - Dynamically loads gtag.js script based on environment
 * - Automatically tracks page views on route changes
 * - Only operates in production to avoid polluting analytics data
 */
export function useGoogleAnalytics() {
  const location = useLocation();

  useEffect(() => {
    // Skip initialization in non-production environments
    if (import.meta.env?.MODE !== 'production') {
      console.log('[GA4] Analytics disabled in development mode');
      return;
    }

    // Skip if no measurement ID is configured
    if (!import.meta.env?.VITE_GA_MEASUREMENT_ID) {
      console.warn('[GA4] No measurement ID configured. Set VITE_GA_MEASUREMENT_ID in .env file');
      return;
    }

    // Initialize gtag.js if not already done
    if (!window.dataLayer) {
      // Load gtag.js script dynamically
      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtag/js?id=${import.meta.env?.VITE_GA_MEASUREMENT_ID}`;
      script.async = true;
      document.head?.appendChild(script);

      // Initialize dataLayer and gtag function
      window.dataLayer = window.dataLayer || [];
      window.gtag = function gtag() {
        window.dataLayer?.push(arguments);
      };
      
      // Initialize GA4 with configuration
      window.gtag('js', new Date());
      window.gtag('config', import.meta.env?.VITE_GA_MEASUREMENT_ID, {
        // Enable debug mode in development (if needed for testing)
        // debug_mode: import.meta.env.MODE === 'development',
        send_page_view: false, // We handle page views manually
      });

      console.log('[GA4] Analytics initialized successfully');
    }

    // Send page_view event on initial load and route changes
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: location?.pathname + location?.search,
        page_title: document.title,
      });

      console.log('[GA4] Page view tracked:', location?.pathname);
    }
  }, [location]);
}